// src/components/facilityEvents/eventStatus.js
//
// The owner's events on the facility page (ShowFacility.jsx): which group an
// event belongs to, in what order, and how its time range reads. Pure
// functions, kept apart from the components so they are easy to test.

// Event type values stored in event.type; labels come from i18n
// (showFacility.eventType.<key>), colours are for the type chip.
export const EVENT_TYPE_VALUES = [
  { key: 'info', value: 0, color: '#1565c0' },
  { key: 'promo', value: 1, color: '#c2410c' },
  { key: 'closure', value: 2, color: '#b91c1c' },
  { key: 'dishOfDay', value: 3, color: '#0f7a4f' },
  { key: 'other', value: 99, color: '#475569' },
];

export const STATUS = { CURRENT: 'CURRENT', UPCOMING: 'UPCOMING', PAST: 'PAST', INACTIVE: 'INACTIVE' };

/** Deactivated first; otherwise by its dates against `now`. */
export function eventStatus(ev, now = new Date()) {
  if (Number(ev.activated) === 0) return STATUS.INACTIVE;
  const start = new Date(ev.date).getTime();
  const end = new Date(ev.end_date).getTime();
  const t = now.getTime();
  if (Number.isFinite(end) && end < t) return STATUS.PAST;
  if (Number.isFinite(start) && start > t) return STATUS.UPCOMING;
  return STATUS.CURRENT;
}

/** { current, upcoming, past }: current by end (soonest first), upcoming by
 *  start, past and deactivated together, newest first. */
export function groupEvents(events, now = new Date()) {
  const current = [];
  const upcoming = [];
  const past = [];
  for (const ev of events || []) {
    const s = eventStatus(ev, now);
    if (s === STATUS.CURRENT) current.push(ev);
    else if (s === STATUS.UPCOMING) upcoming.push(ev);
    else past.push(ev);
  }
  const at = (d) => new Date(d).getTime() || 0;
  current.sort((a, b) => at(a.end_date) - at(b.end_date));
  upcoming.sort((a, b) => at(a.date) - at(b.date));
  past.sort((a, b) => at(b.end_date) - at(a.end_date));
  return { current, upcoming, past };
}

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** « Aujourd'hui 11:00 → 15:00 », « Demain 18:00 → 23:00 », « lun. 13 oct. 11:00 → 15:00 »,
 *  or both full dates when it spans several days. labels: { today, tomorrow }. */
export function formatRange(ev, lang, now = new Date(), labels = {}) {
  const start = new Date(ev.date);
  const end = new Date(ev.end_date);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';
  const time = (d) => d.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
  const day = (d) => d.toLocaleDateString(lang, { weekday: 'short', day: 'numeric', month: 'short' });
  if (!sameDay(start, end)) return `${day(start)} ${time(start)} → ${day(end)} ${time(end)}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const dayLabel = sameDay(start, now) ? labels.today || day(start)
    : sameDay(start, tomorrow) ? labels.tomorrow || day(start)
    : day(start);
  return `${dayLabel} ${time(start)} → ${time(end)}`;
}

/** Date → value of an <input type="datetime-local"> (local time). */
export function toLocalInput(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

export const fromLocalInput = (s) => new Date(s).toISOString();
